/**
 * RTK Query service for the B2C dashboard composite endpoint.
 *
 * GET /api/v1/b2c/composite/dashboard/init-lean returns:
 *   { pipeline, application_status, current_offer, amortization, document_summary }
 *
 * transformResponse synthesises the borrowerProfile and documentSummary
 * shapes expected by the dashboard from the lean payload, eliminating the
 * need for separate borrower_profile and documents API calls.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';

export const compositeInitAPI = createApi({
  reducerPath: 'compositeInitAPI',
  tagTypes: ['dashboardInit'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getDashboardInit: builder.query({
      query: (applicationId) => ({
        url: applicationId
          ? `b2c/composite/dashboard/init-lean?application_id=${encodeURIComponent(applicationId)}`
          : 'b2c/composite/dashboard/init-lean',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => {
        const d = response?.data ?? {};
        const apps = d.pipeline?.applications ?? [];
        // The backend echoes the app it resolved for offer/amortization/docs.
        // Synthesize borrowerProfile from THAT app so the Profile card and
        // the per-app cards stay aligned. Falls back to apps[0] (matches the
        // backend's own fallback) when the target isn't echoed.
        const targetId = d.target_application_id;
        const targetApp =
          (targetId &&
            apps.find(
              (a) => String(a.application_details?.applicationId) === String(targetId)
            )) ||
          apps[0];

        return {
          pipeline: { data: d.pipeline ?? { applications: [] } },
          applicationStatus: { data: d.application_status?.data ?? d.application_status ?? [] },
          currentOffer: { data: d.current_offer ?? null },
          amortization: { data: d.amortization ?? null, success: !!d.amortization },
          borrowerProfile: targetApp
            ? {
                data: {
                  personal_details: {
                    email: targetApp.user_profile?.email,
                    userProfileId: targetApp.user_profile?.borrowerId,
                  },
                  completionStatus: targetApp.application_details?.completionStatus ?? 0,
                },
              }
            : { data: null },
          documentSummary: d.document_summary ?? { total: 0, completed: 0, pending: 0 },
          targetApplicationId: targetId ?? targetApp?.application_details?.applicationId ?? null,
        };
      },
      keepUnusedDataFor: 120,
      providesTags: ['dashboardInit'],
    }),
  }),
});

export const { useGetDashboardInitQuery } = compositeInitAPI;
